/**
 * 작성자 : 김광민
 * 날짜 : 2024.02.02
 * 경로 : 구역별 상황판 > 유해가스 종합현황 (원형, 수치형)
 */

import styled from 'styled-components';
import { v4 } from 'uuid';
import PointIcon from '../../tunnel/PointIcon';
import { convertCO2PpmToPercent, convertCO2PpmToMgPerCubicMeter } from '../../../utils/convertCO2Unit';

const Root = styled.div`
  width: 50%;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  .row.header {
    flex-grow: 1;
    width: 100%;
    display: flex;
    justify-content: center;
    gap: 0.25rem;
    align-items: center;
    max-height: 2.5rem;
    height: 2.5rem;
    .name {
      font-size: 0.875rem;
      font-weight: 400;
      padding: 0.5rem 0.75rem;
      border-radius: 1rem;
      color: var(--text-reverse);
      line-height: 1.25rem;
      display: flex;
      align-items: center;
      gap: 0.375rem;
      height: 1.5rem;
      justify-content: center;
      margin-right: 1rem;
    }
    .nameOnly {
      font-size: 0.875rem;
      color: var(--text-tertiary);
    }
    .material-symbols-rounded {
      font-variation-settings: 'FILL' 0, 'wght' 600, 'GRAD' 0, 'opsz' 24;
      font-size: 1.125rem;
      height: 1.25rem;
    }
    &.startPoint {
      .name {
        background-color: var(--filled-blue-deep);
      }
      .material-symbols-rounded {
        color: var(--primary);
      }
    }
    &.endPoint {
      .name {
        background-color: var(--filled-amber-deep);
      }
      .material-symbols-rounded {
        color: var(--filled-amber);
      }
    }
  }
  .row {
    display: flex;
    align-items: center;
    justify-content: center;
    .textLight {
      text-align: center;
      min-width: 3rem;
      font-size: 0.875rem;
      padding: 0 0.25rem;
      border-radius: 0.25rem;
      color: var(--text-disabled);
    }
    .blue {
      color: var(--primary);
      background-color: var(--tonal-blue);
    }
    .red {
      color: var(--filled-red);
      background-color: var(--tonal-red);
    }
    .violet {
      color: var(--filled-violet);
      background-color: var(--tonal-violet);
    }
    .orange {
      color: var(--filled-orange);
      background-color: var(--tonal-orange);
    }
    .green {
      color: var(--filled-green);
      background-color: var(--tonal-green);
    }
    .amber {
      color: var(--filled-amber);
      background-color: var(--tonal-amber);
    }
    .slate {
      color: var(--filled-slate);
      background-color: var(--tonal);
    }
    .removeBackground {
      background-color: transparent;
    }
    .font-large {
      font-size: 1.375rem;
    }
  }
`;

type TareaListProps = {
  list: any;
  sensorList: any;
  valueType: 'shape' | 'number';
  title?: string;
  allSensorRanges?: { [key: string]: { level1: number; level2: number; level3: number; unit?: string } };
};

const TareaData = ({ list, sensorList, valueType, title, allSensorRanges }: TareaListProps) => {
  const isStartPoint = list.tatGubun === 's';
  const isShapeType = valueType === 'shape';

  const isShapeValue = (level: string) => ['0', '1', '2', '3'].includes(level);
  // 레벨 값이 0, 1, 2, 3 중 하나인지 여부
  const isDataValue = (data: string | null) => data === 'NC' || data === null;
  // 데이터 값이 NC, null 중 하나인지 여부

  // type 값을 센서 코드로 변환하는 함수
  const getSensorCodeByType = (type: string): string => {
    const typeMap: { [key: string]: string } = {
      '1': 'O2',
      '2': 'NO2',
      '3': 'CO',
      '4': 'CO2',
      '5': 'H2S',
      '6': 'CH4',
      '7': 'CH2O',
      '8': 'O3',
      '9': 'PM2.5',
      '10': 'PM10',
      '11': 'TEMP',
      '12': 'HUMI',
      '13': 'VOC',
      '14': 'PM1',
    };
    return typeMap[type] || '';
  };

  // CO2 ppm 값을 unit에 따라 변환하는 함수
  const convertCO2Value = (ppmValue: number | string | undefined): number => {
    if (ppmValue === undefined || ppmValue === null || ppmValue === '') {
      return 0;
    }
    const numValue = typeof ppmValue === 'string' ? Number(ppmValue) : ppmValue;
    if (isNaN(numValue)) {
      return 0;
    }

    const range = allSensorRanges?.CO2;
    if (!range?.unit) {
      return numValue;
    }

    const { unit } = range;
    switch (unit) {
      case '%':
        return convertCO2PpmToPercent(numValue);
      case 'mg/m³':
      case 'mg':
        return convertCO2PpmToMgPerCubicMeter(numValue);
      case 'ppm':
      default:
        return numValue;
    }
  };

  // 센서 값과 range를 비교하여 level을 계산하는 함수
  const calculateLevel = (sensorCode: string, sensorValue: number | string | undefined): string => {
    if (!allSensorRanges || sensorValue === undefined || sensorValue === null || sensorValue === '') {
      return '';
    }

    const numValue = typeof sensorValue === 'string' ? Number(sensorValue) : sensorValue;
    if (isNaN(numValue)) {
      return '';
    }

    const range = allSensorRanges[sensorCode];
    if (!range || !range.level1 || !range.level2 || !range.level3) {
      return '';
    }

    // CO2의 경우 변환된 값으로 비교
    let valueToCompare = numValue;
    if (sensorCode === 'CO2' && range.unit) {
      valueToCompare = convertCO2Value(numValue);
    }

    const { level1, level2, level3 } = range;

    // level0 (좋음): 값 < level1
    // level1 (주의): level1 <= 값 < level2
    // level2 (경고): level2 <= 값 < level3
    // level3 (위험): level3 <= 값
    if (valueToCompare < level1) {
      return '0';
    }
    if (valueToCompare >= level1 && valueToCompare < level2) {
      return '1';
    }
    if (valueToCompare >= level2 && valueToCompare < level3) {
      return '2';
    }
    return '3';
  };

  const getValue = (props: any) => {
    const dataProp = `data${props}`;
    const dataValue = list[dataProp];

    if (isShapeType) {
      // valueType이 shape일 때
      if (!dataValue || dataValue === 'NC' || dataValue === null || dataValue === '') {
        return '-';
      }

      // 센서 코드 가져오기
      const sensorCode = getSensorCodeByType(props);
      if (!sensorCode) {
        return '-';
      }

      // 프론트에서 level 계산
      const levelValue = calculateLevel(sensorCode, dataValue);
      return isShapeValue(levelValue) ? '●' : '-';
    }
    // valueType이 number일 때
    return isDataValue(dataValue) || isNaN(Number(dataValue)) ? '-' : Number(dataValue).toLocaleString();
    // 데이터 값이 NC, null, NaN일 때 -
  };

  const getClassName = (levelValue: string) => {
    const colorMap: { [key: string]: string } = { '0': 'blue', '1': 'green', '2': 'amber', '3': 'red' };
    return colorMap[levelValue] || '';
  };

  const setStyle = (sensor: any) => {
    const defaultStyle = 'textLight';
    const dataProp = `data${sensor.etc1}`;
    const dataValue = list[dataProp];

    let levelValue = '';
    if (dataValue && dataValue !== 'NC' && dataValue !== null && dataValue !== '') {
      const sensorCode = getSensorCodeByType(sensor.etc1);
      if (sensorCode) {
        levelValue = calculateLevel(sensorCode, dataValue);
      }
    }

    const color = getClassName(levelValue);
    const removeBackground = isShapeType ? 'removeBackground' : '';
    const fontSize = isShapeType && getValue(sensor.etc1) !== '-' ? 'font-large' : '';
    return `${defaultStyle} ${color} ${removeBackground} ${fontSize}`;
  };

  const renderName = () => {
    if (!title) {
      return (
        <>
          {isStartPoint ? <PointIcon isStart /> : <PointIcon isStart={false} />}
          <span className='name'>{isStartPoint ? '시점' : '종점'}</span>
        </>
      );
    }
    return <span className='nameOnly'>{title}</span>;
  };

  return (
    <Root>
      <div className={`row header ${isStartPoint ? 'startPoint' : 'endPoint'}`}>{renderName()}</div>
      {sensorList.map((sensor: any) => (
        <div key={v4()} className='row'>
          <span className={setStyle(sensor)}>{getValue(sensor.etc1)}</span>
        </div>
      ))}
    </Root>
  );
};

export default TareaData;
